import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, Coins, CreditCard, MapPin } from "lucide-react";
import clsx from "clsx";
import { PAKISTAN_PROVINCES } from "@aurelia/types";
import type { Address } from "@aurelia/types";
import { EmptyState, PriceTag, Skeleton, formatCents } from "@aurelia/ui";
import { useCart, useCartWithShipping } from "../../features/cart/queries";
import { useInitiateSafepay, usePlaceOrder, useValidateDiscount } from "../../features/orders/queries";
import { useStoreConfig } from "../../features/config/queries";
import { useWallet } from "../../features/wallet/queries";
import { useAddresses } from "../../features/addresses/queries";
import { useDocumentMeta } from "../../lib/useDocumentMeta";
import { PaymentInstructions, hasPaymentDetails } from "../../components/payment/PaymentInstructions";

const COIN_VALUE_CENTS = 100;

const STEPS = ["Cart", "Details", "Confirmation"];

export function CheckoutPage() {
  useDocumentMeta("Checkout", "Complete your order — a small advance now, the rest on delivery.", undefined, undefined, {
    noindex: true
  });
  const { data: cart, isLoading: cartLoading, isError: cartIsError } = useCart();
  const { data: config } = useStoreConfig();
  const { data: wallet } = useWallet();
  const { data: savedAddresses } = useAddresses();
  const placeOrder = usePlaceOrder();
  const validateDiscount = useValidateDiscount();
  const initiateSafepay = useInitiateSafepay();

  const advancePercent = config?.defaultAdvancePercent ?? 20;
  const [line1, setLine1] = useState("");
  const [city, setCity] = useState("");
  const [province, setProvince] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [country, setCountry] = useState("Pakistan");
  const [contactPhone, setContactPhone] = useState("");
  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(null);
  const [discountInput, setDiscountInput] = useState("");
  const [appliedDiscount, setAppliedDiscount] = useState<{ code: string; discountAmount: number } | null>(null);
  const [discountError, setDiscountError] = useState("");
  const [useCoins, setUseCoins] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<"ADVANCE_COD" | "SAFEPAY">("ADVANCE_COD");

  const applyAddress = (address: Address) => {
    setSelectedAddressId(address.id);
    setLine1(address.line1);
    setCity(address.city);
    setProvince(address.province);
    setPostalCode(address.postalCode);
    setCountry(address.country);
    setContactPhone(address.phone);
  };

  // Shipping rates are per-province — nothing to show until one's picked
  // below, then this refetches live as the selection changes.
  const shippingQuery = useCartWithShipping(province || undefined);
  const shippingFee = shippingQuery.data?.shippingFee ?? null;
  const shippingUnavailable = !!province && !shippingQuery.isFetching && shippingFee === null;

  const subtotal = cart?.subtotal ?? 0;
  const discountAmount = appliedDiscount?.discountAmount ?? 0;
  const walletBalance = wallet?.balance ?? 0;
  const maxCoinsUsable = Math.max(0, Math.min(walletBalance, Math.floor((subtotal - discountAmount) / COIN_VALUE_CENTS)));
  const coinsToUse = useCoins ? maxCoinsUsable : 0;
  const coinsValueCents = coinsToUse * COIN_VALUE_CENTS;
  const netSubtotal = subtotal - discountAmount - coinsValueCents;
  const advanceAmount = Math.round((netSubtotal * advancePercent) / 100);
  const remainingAmount = netSubtotal - advanceAmount + (shippingFee ?? 0);

  const currentStep = placeOrder.isSuccess ? 2 : 1;

  const handleApplyDiscount = () => {
    if (!discountInput.trim()) return;
    setDiscountError("");
    validateDiscount.mutate(
      { code: discountInput.trim(), subtotal },
      {
        onSuccess: (result) => setAppliedDiscount(result),
        onError: (err) => setDiscountError((err as Error).message ?? "This code can't be applied")
      }
    );
  };

  const handleRemoveDiscount = () => {
    setAppliedDiscount(null);
    setDiscountInput("");
    setDiscountError("");
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    placeOrder.mutate({
      shippingAddress: { line1, city, province, postalCode, country },
      contactPhone,
      discountCode: appliedDiscount?.code,
      coinsToUse: coinsToUse > 0 ? coinsToUse : undefined,
      paymentMethod
    }, {
      onSuccess: (result) => {
        if (result.checkoutUrl) window.location.assign(result.checkoutUrl);
      }
    });
  };

  if (cartLoading) {
    return (
      <main className="max-w-xl mx-auto px-6 py-16 flex flex-col gap-6">
        <Skeleton className="h-8 w-40 mx-auto" />
        <Skeleton className="h-40" />
        <Skeleton className="h-32" />
        <Skeleton className="h-40" />
      </main>
    );
  }

  if (cartIsError) {
    return (
      <main className="max-w-xl mx-auto px-6 py-24">
        <EmptyState tone="error" title="Couldn't load checkout" description="Please refresh and try again." />
      </main>
    );
  }

  if (placeOrder.isSuccess) {
    const order = placeOrder.data.order;
    const whatsappNumber = config?.whatsappNumber?.replace(/[^\d]/g, "");
    const message = encodeURIComponent(
      `Hi! I just placed order ${order.orderNumber} on Latif General Store.\n` +
        `Total: ${formatCents(order.subtotal)} · Advance: ${formatCents(order.advanceAmount)} · Balance on delivery: ${formatCents(order.remainingAmount)}\n` +
        `I've sent the advance payment — attaching the payment slip below. Please confirm my order. Thank you!`
    );
    const payment = config?.payment;

    return (
      <main className="max-w-xl mx-auto px-6 py-24 text-center flex flex-col items-center gap-6">
        <div className="h-16 w-16 rounded-full bg-emerald-500/10 text-emerald-500 flex items-center justify-center">
          <Check size={28} />
        </div>
        <h1 className="text-display-sm">{order.paymentMethod === "SAFEPAY" ? "Order created" : "Order placed"}</h1>
        <p className="text-ink-muted">
          Order <span className="font-medium text-ink">{order.orderNumber}</span>{" "}
          {order.paymentMethod === "SAFEPAY" ? "is awaiting verified payment." : "was received. We'll reach out to arrange delivery."}
        </p>

        <div className="w-full rounded-2xl border border-border bg-surface p-6 flex flex-col gap-2 text-left">
          <div className="flex justify-between text-sm">
            <span className="text-ink-muted">Subtotal</span>
            <PriceTag cents={order.subtotal} />
          </div>
          {order.discountAmount > 0 && (
            <div className="flex justify-between text-sm text-emerald-600 dark:text-emerald-400">
              <span>Discount{order.discountCodeSnapshot ? ` (${order.discountCodeSnapshot})` : ""}</span>
              <span>-<PriceTag cents={order.discountAmount} className="inline" /></span>
            </div>
          )}
          {order.coinsUsed > 0 && (
            <div className="flex justify-between text-sm text-emerald-600 dark:text-emerald-400">
              <span>Coins applied ({order.coinsUsed})</span>
              <span>-<PriceTag cents={order.coinsUsed * 100} className="inline" /></span>
            </div>
          )}
          <div className="flex justify-between text-sm">
            <span className="text-ink-muted">Shipping</span>
            <PriceTag cents={order.shippingFee} />
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-ink-muted">{order.paymentMethod === "SAFEPAY" ? "Safepay total" : "Advance paid now"}</span>
            <PriceTag cents={order.advanceAmount} />
          </div>
          {order.paymentMethod !== "SAFEPAY" && <div className="flex justify-between text-sm font-medium">
            <span>Balance on delivery</span>
            <PriceTag cents={order.remainingAmount} />
          </div>}
        </div>

        {order.paymentMethod === "ADVANCE_COD" && hasPaymentDetails(payment) && (
          <PaymentInstructions payment={payment} advanceAmount={order.advanceAmount} />
        )}

        {order.paymentMethod === "SAFEPAY" && (
          <div className="w-full rounded-2xl border border-border bg-surface p-5 text-left">
            <p className="font-medium">Safepay payment: {order.paymentStatus.toLowerCase()}</p>
            <p className="mt-1 text-sm text-ink-muted">
              Your order is saved, but it is only treated as paid after Safepay's verified notification arrives.
            </p>
            {placeOrder.data.paymentError && <p className="mt-2 text-sm text-red-500">{placeOrder.data.paymentError}</p>}
            <button
              type="button"
              disabled={initiateSafepay.isPending}
              onClick={() => initiateSafepay.mutate(order.id, { onSuccess: (r) => window.location.assign(r.checkoutUrl) })}
              className="mt-4 h-12 w-full rounded-full bg-ink text-canvas font-medium disabled:opacity-40 dark:bg-accent dark:text-ink"
            >
              {initiateSafepay.isPending ? "Opening Safepay…" : "Continue to Safepay"}
            </button>
            {initiateSafepay.isError && <p className="mt-2 text-sm text-red-500">{initiateSafepay.error.message}</p>}
          </div>
        )}

        {order.coinsEarned > 0 && (
          <div className="w-full flex items-center justify-center gap-2 rounded-full bg-accent-soft text-accent-strong px-4 py-2 text-sm font-medium">
            <Coins size={16} />
            You'll earn {order.coinsEarned} coin{order.coinsEarned === 1 ? "" : "s"} once this order is delivered!
          </div>
        )}

        {order.paymentMethod === "ADVANCE_COD" && whatsappNumber && (
          <a
            href={`https://wa.me/${whatsappNumber}?text=${message}`}
            target="_blank"
            rel="noreferrer"
            className="w-full h-14 rounded-full bg-[#25D366] text-white font-medium flex items-center justify-center gap-2 hover:bg-[#1ebe5a] transition-colors duration-300"
          >
            Confirm via WhatsApp
          </a>
        )}

        <Link to="/account/orders" className="text-sm text-accent hover:underline">
          View my orders
        </Link>
      </main>
    );
  }

  return (
    <main className="max-w-xl mx-auto px-6 py-16">
      <div className="flex items-center justify-center gap-2 mb-12">
        {STEPS.map((step, i) => (
          <div key={step} className="flex items-center gap-2">
            <div
              className={clsx(
                "h-8 w-8 rounded-full flex items-center justify-center text-xs font-medium",
                i <= currentStep ? "bg-accent text-ink" : "bg-surface text-ink-muted border border-border"
              )}
            >
              {i + 1}
            </div>
            <span className={clsx("hidden sm:inline text-sm", i <= currentStep ? "text-ink" : "text-ink-muted")}>
              {step}
            </span>
            {i < STEPS.length - 1 && <div className="w-8 h-px bg-border mx-1" />}
          </div>
        ))}
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-6">
        {!!savedAddresses?.length && (
          <div>
            <p className="text-sm font-medium text-ink-muted mb-2">Use a saved address</p>
            <div className="flex flex-col gap-2">
              {savedAddresses.map((address) => (
                <button
                  key={address.id}
                  type="button"
                  onClick={() => applyAddress(address)}
                  className={clsx(
                    "text-left rounded-xl border px-4 py-3 flex items-start gap-3 transition-colors duration-300",
                    selectedAddressId === address.id
                      ? "border-accent bg-accent-soft"
                      : "border-border bg-surface hover:border-accent/60"
                  )}
                >
                  <MapPin size={16} className="text-ink-muted shrink-0 mt-0.5" />
                  <span className="text-sm">
                    <span className="font-medium">{address.label || address.fullName}</span>
                    <span className="text-ink-muted">
                      {" — "}
                      {address.line1}, {address.city}, {address.province}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="grid grid-cols-1 gap-4">
          <input
            required
            aria-label="Address line"
            placeholder="Address line"
            value={line1}
            onChange={(e) => setLine1(e.target.value)}
            className="h-12 rounded-xl border border-border bg-surface px-4 text-sm"
          />
          <div className="grid grid-cols-2 gap-4">
            <input
              required
              aria-label="City"
              placeholder="City"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              className="h-12 rounded-xl border border-border bg-surface px-4 text-sm"
            />
            <input
              required
              aria-label="Postal code"
              placeholder="Postal code"
              value={postalCode}
              onChange={(e) => setPostalCode(e.target.value)}
              className="h-12 rounded-xl border border-border bg-surface px-4 text-sm"
            />
          </div>
          <select
            required
            aria-label="Province / region"
            value={province}
            onChange={(e) => setProvince(e.target.value)}
            className="h-12 rounded-xl border border-border bg-surface px-4 text-sm"
          >
            <option value="" disabled>
              Select province / region
            </option>
            {PAKISTAN_PROVINCES.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
          {shippingUnavailable && (
            <p className="text-sm text-red-500 -mt-2">
              We don't have a delivery rate set up for this province yet — please contact us to arrange delivery.
            </p>
          )}
          <input
            required
            aria-label="Country"
            placeholder="Country"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            className="h-12 rounded-xl border border-border bg-surface px-4 text-sm"
          />
          <input
            required
            type="tel"
            aria-label="Contact phone"
            placeholder="Contact phone"
            value={contactPhone}
            onChange={(e) => setContactPhone(e.target.value)}
            className="h-12 rounded-xl border border-border bg-surface px-4 text-sm"
          />
        </div>

        <div className="grid gap-3">
          <p className="text-sm font-medium text-ink-muted">Payment method</p>
          <button
            type="button"
            onClick={() => setPaymentMethod("ADVANCE_COD")}
            className={clsx("rounded-xl border px-4 py-3 text-left", paymentMethod === "ADVANCE_COD" ? "border-accent bg-accent-soft" : "border-border bg-surface")}
          >
            <span className="text-sm font-medium">{advancePercent}% advance, rest on delivery</span>
            <span className="mt-0.5 block text-sm text-ink-muted">Pay the advance manually, then pay the balance in cash on delivery.</span>
          </button>
          <button
            type="button"
            onClick={() => setPaymentMethod("SAFEPAY")}
            className={clsx("rounded-xl border px-4 py-3 text-left", paymentMethod === "SAFEPAY" ? "border-accent bg-accent-soft" : "border-border bg-surface")}
          >
            <span className="flex items-center gap-2 text-sm font-medium"><CreditCard size={16} /> Pay online with Safepay</span>
            <span className="mt-0.5 block text-sm text-ink-muted">Pay the full total securely in Safepay's hosted checkout.</span>
          </button>
        </div>

        <div>
          <p className="text-sm font-medium text-ink-muted mb-2">Discount code</p>
          {appliedDiscount ? (
            <div className="h-12 rounded-xl border border-border bg-surface px-4 flex items-center justify-between text-sm">
              <span>
                <span className="font-medium">{appliedDiscount.code}</span> applied
              </span>
              <button type="button" onClick={handleRemoveDiscount} className="text-xs font-medium text-accent hover:underline">
                Remove
              </button>
            </div>
          ) : (
            <div className="flex gap-2">
              <input
                aria-label="Discount code"
                value={discountInput}
                onChange={(e) => setDiscountInput(e.target.value.toUpperCase())}
                placeholder="Enter code"
                className="flex-1 min-w-0 h-12 rounded-xl border border-border bg-surface px-4 text-sm"
              />
              <button
                type="button"
                onClick={handleApplyDiscount}
                disabled={validateDiscount.isPending || !discountInput.trim()}
                className="h-12 px-5 rounded-xl bg-ink text-canvas text-sm font-medium hover:bg-accent hover:text-ink transition-colors duration-300 disabled:opacity-40 dark:bg-accent dark:text-ink"
              >
                {validateDiscount.isPending ? "Checking…" : "Apply"}
              </button>
            </div>
          )}
          {discountError && <p className="text-sm text-red-500 mt-2">{discountError}</p>}
        </div>

        {walletBalance > 0 && (
          <div className="rounded-2xl border border-border bg-surface p-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-accent-soft text-accent-strong flex items-center justify-center shrink-0">
                <Coins size={18} />
              </div>
              <p className="text-sm">
                You have <span className="font-medium">{walletBalance} coins</span> (worth Rs {walletBalance})
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={useCoins}
              aria-label={`Use ${walletBalance} reward coins on this order`}
              onClick={() => setUseCoins((v) => !v)}
              className={clsx(
                "inline-flex h-7 w-12 shrink-0 rounded-full transition-colors duration-300 relative",
                useCoins ? "bg-accent" : "bg-border"
              )}
            >
              <span
                className={clsx(
                  "absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-transform duration-300",
                  useCoins ? "translate-x-6" : "translate-x-1"
                )}
              />
            </button>
          </div>
        )}

        <div className="rounded-2xl border border-border bg-surface p-6 flex flex-col gap-2">
          <div className="flex justify-between text-sm">
            <span className="text-ink-muted">Subtotal</span>
            <PriceTag cents={subtotal} />
          </div>
          {appliedDiscount && (
            <div className="flex justify-between text-sm text-emerald-600 dark:text-emerald-400">
              <span>Discount ({appliedDiscount.code})</span>
              <span>-<PriceTag cents={discountAmount} className="inline" /></span>
            </div>
          )}
          {coinsToUse > 0 && (
            <div className="flex justify-between text-sm text-emerald-600 dark:text-emerald-400">
              <span>Coins applied ({coinsToUse})</span>
              <span>-<PriceTag cents={coinsValueCents} className="inline" /></span>
            </div>
          )}
          <div className="flex justify-between text-sm">
            <span className="text-ink-muted">Shipping</span>
            {!province ? (
              <span className="text-ink-muted">Select a province below</span>
            ) : shippingQuery.isFetching ? (
              <span className="text-ink-muted">Calculating…</span>
            ) : shippingUnavailable ? (
              <span className="text-red-500">Not available</span>
            ) : (
              <PriceTag cents={shippingFee ?? 0} />
            )}
          </div>
          {paymentMethod === "SAFEPAY" ? (
            <div className="flex justify-between text-sm font-medium">
              <span>Total payable with Safepay</span>
              <PriceTag cents={netSubtotal + (shippingFee ?? 0)} />
            </div>
          ) : (
            <>
              <div className="flex justify-between text-sm">
                <span className="text-ink-muted">Advance now ({advancePercent}%)</span>
                <PriceTag cents={advanceAmount} />
              </div>
              <div className="flex justify-between text-sm font-medium">
                <span>Balance on delivery</span>
                <PriceTag cents={remainingAmount} />
              </div>
            </>
          )}
        </div>

        {placeOrder.isError && <p className="text-sm text-red-500">{(placeOrder.error as Error).message}</p>}

        <button
          type="submit"
          disabled={placeOrder.isPending || !cart?.items.length || !province || shippingFee === null}
          className="w-full h-14 rounded-full bg-ink text-canvas font-medium tracking-tight hover:bg-accent hover:text-ink transition-colors duration-300 ease-physical disabled:opacity-40 dark:bg-accent dark:text-ink"
        >
          {placeOrder.isPending ? "Placing order…" : paymentMethod === "SAFEPAY" ? "Continue to Safepay" : "Place order"}
        </button>
      </form>
    </main>
  );
}
